// store.js
import { create } from "zustand";
import { persist } from "zustand/middleware";

// Main store (non-persisted)
const useStore = create((set) => ({
  keycloakInstance: null,

  keycloakInitFailed: false,

  accessToken: null,

  tokenParsed: null,

  authenticated: false,

  selectedChannel: null,

  calendarEvents: [],

  // Sidebar state
  desktopSidebarOpen: true,

  mobileSidebarOpen: false,

  // Actions
  setKeycloakInstance: (keycloakInstance) => set({ keycloakInstance }),

  setAccessToken: (accessToken) => set({ accessToken }),

  setTokenParsed: (parsed) => set({ tokenParsed: parsed }),

  setSelectedChannel: (channel) => set({ selectedChannel: channel }),

  setCalendarEvents: (events) => set({ calendarEvents: events }),

  // Sidebar actions
  setDesktopSidebarOpen: (isOpen) => set({ desktopSidebarOpen: isOpen }),

  setMobileSidebarOpen: (isOpen) => set({ mobileSidebarOpen: isOpen }),
}));

const MAX_CLICKED_ENTRIES = 2000;

/**
 * Prune a clicked-items map to keep only the most recent entries.
 * JS objects preserve insertion order for string keys, so we drop the oldest.
 */
const pruneClicked = (obj) => {
  const keys = Object.keys(obj);
  if (keys.length <= MAX_CLICKED_ENTRIES) return obj;
  const kept = keys.slice(-MAX_CLICKED_ENTRIES);
  const result = {};
  for (const k of kept) result[k] = obj[k];
  return result;
};

// Font store (persisted under 'font-storage')
const useFontStore = create(
  persist(
    (set) => ({
      clickedHeadlines: {},
      toggleHeadlineFont: (id) =>
        set((state) => ({
          clickedHeadlines: pruneClicked({
            ...state.clickedHeadlines,
            [id]: !state.clickedHeadlines[id],
          }),
        })),

      clickedNewsquawkHeadlines: {},
      toggleNewsquawkHeadlineFont: (id) =>
        set((state) => ({
          clickedNewsquawkHeadlines: pruneClicked({
            ...state.clickedNewsquawkHeadlines,
            [id]: !state.clickedNewsquawkHeadlines[id],
          }),
        })),

      clickedEnergyTweets: {},
      toggleEnergyTweetsFont: (id) =>
        set((state) => ({
          clickedEnergyTweets: pruneClicked({
            ...state.clickedEnergyTweets,
            [id]: !state.clickedEnergyTweets[id],
          }),
        })),

      clickedEnergyNewsquawkHeadlines: {},
      toggleEnergyNewsquawkHeadlinesFont: (id) =>
        set((state) => ({
          clickedEnergyNewsquawkHeadlines: pruneClicked({
            ...state.clickedEnergyNewsquawkHeadlines,
            [id]: !state.clickedEnergyNewsquawkHeadlines[id],
          }),
        })),

      clickedFinTweets: {},
      toggleFinTweetsFont: (id) =>
        set((state) => ({
          clickedFinTweets: pruneClicked({
            ...state.clickedFinTweets,
            [id]: !state.clickedFinTweets[id],
          }),
        })),

      resetClickedNewsquawkHeadlines: () =>
        set(() => ({
          clickedNewsquawkHeadlines: {},
        })),

      clickedFirehoseStories: {},

      toggleFirehoseStoryClicked: (story_id) =>
        set((state) => ({
          clickedFirehoseStories: pruneClicked({
            ...state.clickedFirehoseStories,
            [story_id]: !state.clickedFirehoseStories[story_id],
          }),
        })),
    }),
    { name: "font-storage" }
  )
);

// Newsquawk headlines cache store (persisted under 'newsquawk-headlines-storage')
const useNewsquawkHeadlinesCacheStore = create(
  persist(
    (set) => ({
      newsquawkHeadlinesCache: [],
      setNewsquawkHeadlinesCache: (headlines) =>
        set({ newsquawkHeadlinesCache: headlines }),
    }),
    { name: "newsquawk-headlines-storage" }
  )
);

// Research cache store (persisted under 'research-storage')
const useResearchCacheStore = create(
  persist(
    (set) => ({
      researchCache: [],
      setResearchCache: (articles) => set({ researchCache: articles }),
    }),
    { name: "research-storage" }
  )
);

export {
  useStore,
  useFontStore,
  useNewsquawkHeadlinesCacheStore,
  useResearchCacheStore,
};
